import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { IconButton, TextField } from '@material-ui/core'
import DeleteIcon from '@material-ui/icons/Delete'
import EditIcon from '@material-ui/icons/Edit'
import {
  SourceQueryDisplay,
  TablesDisplay,
  ColumnsDisplay,
  PaginationDisplay
} from '../configurationDisplay'
import BasicTable from '../../../../../../atoms/BasicTable'
import CodeTag from '../../../../../../atoms/CodeTag'
import RoundedModal from '../../../../../../molecules/RoundedModal'
import Switch from '../../../../../../molecules/Switch'
import SydButton from '../../../../../../commonDesign/Button'
import SydLabel from '../../../../../../commonDesign/SydLabel'
import PredicatesEditor from './PredicatesEditor'
import EditPurposeDqlModal from './EditPurposeDqlModal'
import RenamePurposeModal from './RenamePurposeModal'

function buildDefaultLatch (dateTable) {
  return { parameterName: 'snapshotDate', dateColumn: 'snapshotDate', dateTable, limit: 100 }
}

function PurposeDetailPanel ({ purposeCode, purposeData, onUpdatePurpose }) {
  const [editModal, setEditModal] = useState({ open: false, index: null, predicate: null })
  const [editDqlOpen, setEditDqlOpen] = useState(false)
  const [renameOpen, setRenameOpen] = useState(false)

  const handleAddPredicate = useCallback(() => {
    setEditModal({ open: true, index: null, predicate: null })
  }, [])

  const handleEditPredicate = useCallback((index) => {
    if (!purposeData) return
    setEditModal({
      open: true,
      index,
      predicate: purposeData.subordinatePredicates[index]
    })
  }, [purposeData])

  const handleCloseModal = useCallback(() => {
    setEditModal({ open: false, index: null, predicate: null })
  }, [])

  const handleSavePredicate = useCallback((saved) => {
    if (!purposeCode || !purposeData) return
    const current = purposeData.subordinatePredicates || []

    if (editModal.index !== null) {
      const updated = current.map((p, i) => i === editModal.index ? saved : p)
      onUpdatePurpose(purposeCode, { ...purposeData, subordinatePredicates: updated })
    } else {
      onUpdatePurpose(purposeCode, { ...purposeData, subordinatePredicates: [...current, saved] })
    }
    handleCloseModal()
  }, [purposeCode, purposeData, editModal.index, onUpdatePurpose, handleCloseModal])

  const handleRemovePredicate = useCallback((index) => {
    if (!purposeCode || !purposeData) return
    const updated = (purposeData.subordinatePredicates || []).filter((_, i) => i !== index)
    onUpdatePurpose(purposeCode, { ...purposeData, subordinatePredicates: updated })
  }, [purposeCode, purposeData, onUpdatePurpose])

  const latchDateTable = useMemo(() => {
    const mainTable = (purposeData?.tables || []).find(t => t.purpose === 'main')
    if (!mainTable) return ''
    return mainTable.schema ? `${mainTable.schema}.${mainTable.tableCode}` : mainTable.tableCode
  }, [purposeData])

  const handleToggleLatch = useCallback((e) => {
    if (!purposeCode || !purposeData) return
    const enabled = e.target.checked
    onUpdatePurpose(purposeCode, {
      ...purposeData,
      latch: enabled ? buildDefaultLatch(latchDateTable) : null
    })
  }, [purposeCode, purposeData, onUpdatePurpose, latchDateTable])

  const handleAutoConfigureLatch = useCallback(() => {
    if (!purposeCode || !purposeData) return
    if (!latchDateTable) {
      window.alert('Unable to auto-configure: no main table found in this purpose. Configure the latch fields manually.')
      return
    }
    onUpdatePurpose(purposeCode, {
      ...purposeData,
      latch: buildDefaultLatch(latchDateTable)
    })
  }, [purposeCode, purposeData, onUpdatePurpose, latchDateTable])

  const handleLatchFieldChange = useCallback((field, value) => {
    if (!purposeCode || !purposeData?.latch) return
    onUpdatePurpose(purposeCode, {
      ...purposeData,
      latch: { ...purposeData.latch, [field]: value }
    })
  }, [purposeCode, purposeData, onUpdatePurpose])

  const handleOpenInQueryTool = useCallback(() => {
    if (!purposeData?.sourceQuery) return
    localStorage.setItem('denaliQueryToolDql', purposeData.sourceQuery)
    window.open('/admin/denali/analysis/catalog/query?fromAssistant=1', '_blank')
  }, [purposeData])

  const handleRename = useCallback((newName) => {
    if (!purposeCode || !purposeData) return
    onUpdatePurpose(purposeCode, { ...purposeData, displayName: newName })
    setRenameOpen(false)
  }, [purposeCode, purposeData, onUpdatePurpose])

  const handleUpdateColumn = useCallback((colIndex, field, value) => {
    if (!purposeCode || !purposeData) return
    const updatedColumns = purposeData.columns.map((col, i) =>
      i === colIndex ? { ...col, [field]: value } : col
    )
    onUpdatePurpose(purposeCode, { ...purposeData, columns: updatedColumns })
  }, [purposeCode, purposeData, onUpdatePurpose])

  if (!purposeCode || !purposeData) {
    return (
      <div style={{
        padding: '32px',
        textAlign: 'center',
        color: 'var(--text-color-subtle)',
        paddingLeft: '32px'
      }}
      >
        <p>Select a purpose to view details</p>
      </div>
    )
  }

  const subordinatePredicates = purposeData.subordinatePredicates || []

  return (
    <div style={{ paddingLeft: '24px', overflowY: 'auto' }}>
      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <h3 style={{ marginBottom: '4px' }}>{purposeData.displayName}</h3>
          <IconButton size='small' onClick={() => setRenameOpen(true)} aria-label='Rename purpose' title='Rename'>
            <EditIcon fontSize='small' />
          </IconButton>
        </div>
        <div style={{ fontSize: '0.875rem', color: 'var(--text-color-subtle)' }}>
          Purpose Code: {purposeCode}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
        <SydButton size='xs' variant='outline' icon='openInNew' onClick={handleOpenInQueryTool}>
          Open in Query Tool
        </SydButton>
        <SydButton size='xs' variant='outline' icon='edit' onClick={() => setEditDqlOpen(true)}>
          Edit Query
        </SydButton>
      </div>
      <SourceQueryDisplay sourceQuery={purposeData.sourceQuery} />
      <TablesDisplay tables={purposeData.tables} />
      <ColumnsDisplay columns={purposeData.columns} onUpdateColumn={handleUpdateColumn} />

      {/* Predicates */}
      <div style={{ marginBottom: '16px' }}>
        <h4>Predicates</h4>

        {/* Main Predicate (read-only) */}
        <div style={{ marginBottom: '12px' }}>
          <h5>Main Predicate</h5>
          <CodeTag>
            {purposeData.masterPredicate?.expression || 'None'}
          </CodeTag>
          {purposeData.masterPredicate?.requiredParams?.length > 0
            ? (
              <p style={{ fontSize: 'var(--text-size-sm)', color: 'var(--text-color-subtle)', marginTop: '4px' }}>
                Parameters: {purposeData.masterPredicate?.requiredParams.join(', ')}
              </p>
            )
            : null}
        </div>

        {/* Filter Predicates */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <h5 style={{ margin: 0 }}>Filter Predicates ({subordinatePredicates.length})</h5>
          <SydButton size='sm' icon='add' onClick={handleAddPredicate}>
            Add Filter
          </SydButton>
        </div>

        {subordinatePredicates.length > 0 ? (
          <BasicTable className='opal-presentation-table zebra-rows'>
            <thead className='opt-thead'>
              <tr className='opt-tr'>
                <th className='opt-th'>Code</th>
                <th className='opt-th'>Description</th>
                <th className='opt-th'>Expression</th>
                <th className='opt-th' style={{ width: '80px' }} />
              </tr>
            </thead>
            <tbody className='opt-tbody'>
              {subordinatePredicates.map((pred, idx) => (
                <tr className='opt-tr' key={pred.code || idx}>
                  <td className='opt-td'>{pred.code}</td>
                  <td className='opt-td'>{pred.description || '-'}</td>
                  <td className='opt-td'>
                    <CodeTag>{pred.expression}</CodeTag>
                  </td>
                  <td className='opt-td'>
                    <div className='flex flex-row gap-sm'>
                      <IconButton size='small' onClick={() => handleEditPredicate(idx)} aria-label='Edit predicate' title='Edit predicate'>
                        <EditIcon fontSize='small' />
                      </IconButton>
                      <IconButton size='small' onClick={() => handleRemovePredicate(idx)} aria-label='Delete predicate' title='Delete predicate'>
                        <DeleteIcon fontSize='small' />
                      </IconButton>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </BasicTable>
        ) : (
          <p style={{ color: 'var(--text-color-subtle)', fontSize: '0.875rem' }}>
            No filter predicates configured.
          </p>
        )}
      </div>

      <RoundedModal
        title={editModal.index !== null ? 'Edit Filter Predicate' : 'Add Filter Predicate'}
        open={editModal.open}
        onClose={handleCloseModal}
        size='medium'
        position='top'
        disablePortal={false}
      >
        {editModal.open
          ? (
            <PredicatesEditor
              predicate={editModal.predicate}
              onSave={handleSavePredicate}
              onCancel={handleCloseModal}
            />
          )
          : <div />}
      </RoundedModal>

      {/* Snapshot Latch */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <h4 style={{ margin: 0 }}>Snapshot Latch</h4>
          <Switch
            checked={!!purposeData.latch}
            onChange={handleToggleLatch}
          />
          {purposeData.latch ? (
            <SydButton size='xs' variant='outline' onClick={handleAutoConfigureLatch}>
              Auto-configure
            </SydButton>
          ) : null}
        </div>
        {purposeData.latch
          ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <SydLabel label='Parameter Name' helperText='Key used when passing the latch date in parameters (e.g., snapshotDate)'>
                <TextField
                  value={purposeData.latch.parameterName || ''}
                  onChange={(e) => handleLatchFieldChange('parameterName', e.target.value)}
                  size='small'
                  fullWidth
                  placeholder='snapshotDate'
                />
              </SydLabel>
              <SydLabel label='Date Column' helperText='Column used to filter results by snapshot date and to query available dates (e.g., snapshotDate)'>
                <TextField
                  value={purposeData.latch.dateColumn || ''}
                  onChange={(e) => handleLatchFieldChange('dateColumn', e.target.value)}
                  size='small'
                  fullWidth
                  placeholder='snapshotDate'
                />
              </SydLabel>
              <SydLabel label='Date Table' helperText='Fully qualified table (e.g., db.account_performance)'>
                <TextField
                  value={purposeData.latch.dateTable || ''}
                  onChange={(e) => handleLatchFieldChange('dateTable', e.target.value)}
                  size='small'
                  fullWidth
                  placeholder='db.account_performance'
                />
              </SydLabel>
              <SydLabel label='Limit' helperText='Max dates to show in selector'>
                <TextField
                  value={purposeData.latch.limit || 100}
                  onChange={(e) => handleLatchFieldChange('limit', parseInt(e.target.value, 10) || 100)}
                  size='small'
                  fullWidth
                  type='number'
                />
              </SydLabel>
            </div>
          )
          : null}
      </div>

      <PaginationDisplay pagination={purposeData.pagination} />

      <EditPurposeDqlModal
        open={editDqlOpen}
        onClose={() => setEditDqlOpen(false)}
        purposeCode={purposeCode}
        purposeData={purposeData}
        onUpdate={onUpdatePurpose}
      />

      <RenamePurposeModal
        open={renameOpen}
        onClose={() => setRenameOpen(false)}
        currentName={purposeData.displayName}
        onSave={handleRename}
      />
    </div>
  )
}

PurposeDetailPanel.propTypes = {
  purposeCode: PropTypes.string,
  purposeData: PropTypes.object,
  onUpdatePurpose: PropTypes.func
}

PurposeDetailPanel.defaultProps = {
  onUpdatePurpose: () => {}
}

export default PurposeDetailPanel
